import * as Dialog from "@radix-ui/react-dialog";
import { DevToolkit } from "@/components/DevToolkit";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import { BarChart3, Handshake, Factory, ShieldAlert, Wand2, Sparkles, TrendingUp, KeyRound, PackageSearch, Activity, ChevronDown, Megaphone, Menu, Boxes, ChevronsUpDown, LayoutDashboard, LogOut, Moon, Network, Package, PlugZap, Search, Settings, Building2, ShieldCheck, ShoppingCart, Sun, Truck, Users, WifiOff, Warehouse as WarehouseIcon, Workflow, MapPinCheck, PackageOpen, Route, RotateCcw, Database, Puzzle, ListChecks, Bell, Gauge, Lock, History, Plug, Euro, Clock3, Mail as MailIcon, ClipboardList, Container, PackageCheck, Bot, Blocks, Languages, Check } from "lucide-react";
import { getLocale, LOCALES, msg, setLocale, syncProfileLocale, t } from "@/lib/i18n";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { api, ApiError, can, canManage, isDemo, type Area, type EffectiveRole, type Me, type Permissions , type ModuleKey } from "@/lib/api";
import { NewCustomerDialog } from "@/pages/Admin";
import { useActionItems } from "@/pages/ActionItems";
import { MfaSetupWizard } from "@/pages/Security";
import { Badge, cx, Kbd, Logo, ValidationBadge } from "./ui";
import { StagingBanner } from "./StagingBanner";
import { GuideProvider, HelpButton } from "./Guide";
import { helpFor } from "@/lib/guide";
import { intlLocale } from "@/lib/i18n";
import { useUnseenChangelog } from "@/lib/changelog";
import { fillOverdueText, prospectAllowsPath, type ProspectAccess } from "@logiful/domain";
import { Lock as LockIcon } from "lucide-react";

/** Banner „überfällige Rechnungen“ – Text von Logiful einstellbar (de/en), Link zu den offenen Rechnungen */
function OverdueBanner({ o, staff }: { o: NonNullable<Me["tenants"][number]["overdue"]>; staff: boolean }) {
  const lang = getLocale() === "en" ? "en" : "de";
  const amount = new Intl.NumberFormat(intlLocale(), { style: "currency", currency: "EUR" }).format(o.amount);
  const pauseDate = o.pauseDate ? new Date(`${o.pauseDate}T12:00:00`).toLocaleDateString(intlLocale()) : undefined;
  return (
    <div role="alert" className="sticky top-0 z-30 border-b border-danger/30 bg-danger-soft px-4 md:px-8 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="font-semibold text-danger">{o.blocked ? t("Aufträge pausiert") : t("Offene Rechnungen")}</span>
      <span className="text-fg">{fillOverdueText(o.text[lang], { count: o.count, amount, oldestDays: o.oldestDays, ...(pauseDate ? { pauseDate } : {}) })}</span>
      {!o.blocked && pauseDate && <span className="rounded-full bg-danger/10 px-2 py-px text-[11.5px] font-medium text-danger">{t("Aufträge werden ab dem {d} pausiert", { d: pauseDate })}</span>}
      <NavLink to="/invoices" className="font-medium text-danger underline underline-offset-2">{t("Zu den offenen Rechnungen")}</NavLink>
      {staff && <span className="text-fg-subtle">{t("(Logiful-Ansicht – so sieht es der Kunde)")}</span>}
    </div>
  );
}

/** Seite für Interessenten noch nicht freigegeben (direkter Link) */
function ProspectLocked() {
  return (
    <div className="px-4 md:px-8 py-16 max-w-lg mx-auto text-center">
      <div className="size-12 mx-auto rounded-2xl bg-surface-2 grid place-items-center"><LockIcon className="size-5 text-fg-muted" /></div>
      <h1 className="mt-4 text-[20px] font-semibold">{t("Bald für euch verfügbar")}</h1>
      <p className="mt-2 text-[14px] text-fg-muted">{t("Dieser Bereich steht nach der Freischaltung durch Logiful zur Verfügung. Euer Ansprechpartner meldet sich bei euch.")}</p>
      <NavLink to="/" className="mt-6 inline-block text-[13.5px] font-medium underline underline-offset-2">{t("Zur Übersicht")}</NavLink>
    </div>
  );
}

// ── Mandant & Theme ───────────────────────────────────────────────────
interface TenantCtx {
  me: Me;
  tenantId: string;
  tenant: Me["tenants"][number];
  /** Rolle im aktuell gewählten Mandanten */
  role: EffectiveRole;
  setTenantId: (id: string) => void;
}
const TenantContext = createContext<TenantCtx | null>(null);
export const useTenant = () => {
  const c = useContext(TenantContext);
  if (!c) throw new Error("useTenant außerhalb von AppShell");
  return c;
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem("lc-theme");
      return saved ? saved === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    try {
      localStorage.setItem("lc-theme", dark ? "dark" : "light");
    } catch {
      /* privater Modus */
    }
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof Package;
  end?: boolean;
  soon?: boolean;
  /** nur für Rollen, die verwalten dürfen (Kunden-Admin, Logiful) */
  manage?: boolean;
  /** nur für Logiful-Mitarbeiter */
  staff?: boolean;
  section?: "main" | "settings" | "logiful";
  /** Zähler am Menüpunkt */
  badge?: "actions" | "alerts" | "changelog";
  /** nur mit Leserecht in diesem Bereich */
  area?: Area;
  /** nur mit gebuchter Modul-Funktion sichtbar (Logiful sieht ihn immer) */
  module?: [ModuleKey, string];
  /** Einstellungen: Thema auf der Übersichtsseite und kurze Erklärung */
  group?: SettingsGroup;
  desc?: string;
}

export type SettingsGroup = "sales" | "stock" | "orders" | "account";
export const SETTINGS_GROUPS: { key: SettingsGroup; title: string }[] = [
  { key: "sales", title: msg("Verkaufskanäle") },
  { key: "stock", title: msg("Lager & Bestand") },
  { key: "orders", title: msg("Aufträge & Versand") },
  { key: "account", title: msg("Konto & Team") },
];

const NAV: NavItem[] = [
  { to: "/", label: msg("Übersicht"), icon: LayoutDashboard, end: true },
  { to: "/todo", label: msg("Handlungsbedarf"), icon: ListChecks, badge: "actions" },
  { to: "/orders", label: msg("Aufträge"), icon: ShoppingCart, area: "orders" },
  { to: "/products", label: msg("Artikel"), icon: Package, area: "products" },
  { to: "/stock", label: msg("Bestände"), icon: Boxes, area: "stock" },
  { to: "/inbounds", label: msg("Wareneingänge"), icon: Truck, area: "inbounds" },
  { to: "/b2b", label: msg("B2B"), icon: Container, area: "orders", module: ["b2b", "b2b_orders"] },
  { to: "/fba", label: msg("Pre-FBA"), icon: PackageCheck, area: "orders", module: ["fba", "prefba"] },
  { to: "/production", label: msg("Produktion"), icon: Factory, area: "production", module: ["production", "orders"] },
  { to: "/purchasing", label: msg("Einkauf"), icon: ClipboardList, area: "inbounds", module: ["purchasing", "purchase_orders"] },
  { to: "/returns", label: msg("Retouren"), icon: RotateCcw, area: "returns" },
  { to: "/tracking", end: true, label: msg("Track & Trace"), icon: PackageSearch, area: "orders", module: ["tracking", "delivery_status"] },
  { to: "/tracking/page", label: msg("Tracking-Seite"), icon: Sparkles, area: "orders", module: ["tracking_page", "page"] },
  { to: "/performance", label: msg("Leistung"), icon: Gauge, area: "reports" },
  { to: "/costs", label: msg("Kosten"), icon: Euro, area: "costs", module: ["analytics", "costs"] },
  { to: "/profit", label: msg("Profit Center"), icon: TrendingUp, area: "profit", module: ["profit", "margins"] },
  { to: "/partners", label: msg("Partner Network"), icon: Handshake },
  { to: "/channels", label: msg("Shops"), icon: PlugZap, manage: true, section: "settings", group: "sales", desc: msg("Shopify, Marktplätze und SFTP verbinden und verwalten") },
  { to: "/settings/delivery-widget", label: msg("Lieferzeit im Shop"), icon: Clock3, manage: true, section: "settings", group: "sales", desc: msg("Lieferversprechen im Shop anzeigen") },
  { to: "/settings/ai", label: msg("KI-Assistent"), icon: Bot, section: "settings", group: "sales", module: ["ai", "assistant"], desc: msg("Claude & Co. mit euren Logiful-Daten verbinden") },
  { to: "/settings/apps", label: msg("Tool-Anbindungen"), icon: Blocks, manage: true, section: "settings", group: "sales", desc: msg("Klaviyo – Versand- und Zustellereignisse für eigene Mails") },
  { to: "/settings/integrations", label: msg("Schnittstelle"), icon: Plug, manage: true, section: "settings", group: "sales", desc: msg("API-Schlüssel und Webhooks für eigene Systeme") },
  { to: "/settings/warehouse", label: msg("Lager"), icon: WarehouseIcon, manage: true, section: "settings", group: "stock", desc: msg("Anbindung an das Logiful-Lager") },
  { to: "/rules/products", label: msg("Artikelregeln"), icon: Wand2, section: "settings", group: "stock", area: "products", desc: msg("Stammdaten automatisch ergänzen – z. B. Gewicht und HS-Code nach Artikelname") },
  { to: "/settings/stock-sync", label: msg("Bestandsabgleich"), icon: Boxes, section: "settings", group: "stock", desc: msg("Welcher Bestand an Shops gemeldet wird – Zulauf, Puffer") },
  { to: "/rules", label: msg("Auftragsregeln"), icon: Workflow, manage: true, section: "settings", end: true, group: "orders", desc: msg("Aufträge automatisch prüfen, zurückhalten, ergänzen") },
  { to: "/rules/shipping", label: msg("Versandregeln"), icon: Route, manage: true, section: "settings", group: "orders", desc: msg("Welcher Versanddienstleister für welchen Auftrag") },
  { to: "/settings/carrier-accounts", label: msg("Versandkonten"), icon: KeyRound, section: "settings", group: "orders", desc: msg("DHL-Zugänge je Versandart – für die Sendungsverfolgung") },
  { to: "/settings/validation", label: msg("Stammdaten-Prüfung"), icon: ShieldAlert, section: "settings", group: "orders", desc: msg("Welche fehlenden Angaben Aufträge zurückhalten (stellt Logiful ein)") },
  { to: "/settings/customer-comms", label: msg("Kundenkommunikation"), icon: MailIcon, manage: true, section: "settings", group: "orders", desc: msg("Mails an eure Endkunden, z. B. Adressklärung") },
  { to: "/settings/modules", label: msg("Module"), icon: Puzzle, section: "settings", group: "account", desc: msg("Gebuchte Funktionen und Pakete") },
  { to: "/settings/team", label: msg("Team"), icon: Users, section: "settings", group: "account", desc: msg("Mitarbeiter einladen und Rechte vergeben") },
  { to: "/settings/notifications", label: msg("Benachrichtigungen"), icon: Bell, section: "settings", group: "account", desc: msg("Wer wann per Mail informiert wird") },
  { to: "/settings/security", label: msg("Sicherheit"), icon: Lock, section: "settings", group: "account", desc: msg("Zwei-Faktor-Anmeldung und Sitzungen") },
  { to: "/settings/audit", label: msg("Änderungsprotokoll"), icon: History, manage: true, section: "settings", group: "account", desc: msg("Wer hat wann was geändert") },
  { to: "/settings/privacy", label: msg("Datenschutz-Anfragen"), icon: ShieldCheck, manage: true, section: "settings", group: "account", desc: msg("Auskunft und Löschung von Endkundendaten aus euren Shops") },
  { to: "/admin/customers", label: msg("Kunden"), icon: Building2, staff: true, section: "logiful" },
  { to: "/admin/ffn", label: msg("FFN-Händler"), icon: Network, staff: true, section: "logiful" },
  { to: "/admin/shipping", label: msg("Versand"), icon: PackageOpen, staff: true, section: "logiful" },
  { to: "/admin/carrier-accounts", label: msg("Versandkonten"), icon: KeyRound, staff: true, section: "logiful" },
  { to: "/admin/address", label: msg("Adressprüfung"), icon: MapPinCheck, staff: true, section: "logiful" },
  { to: "/admin/returns", label: msg("Retouren-Anbindung"), icon: Database, staff: true, section: "logiful" },
  { to: "/admin/modules", label: msg("Module & Preise"), icon: Puzzle, staff: true, section: "logiful" },
  { to: "/admin/news", label: msg("News & Termine"), icon: Megaphone, staff: true, section: "logiful" },
  { to: "/admin/kpis", label: msg("Kennzahlen"), icon: BarChart3, staff: true, section: "logiful" },
  { to: "/admin/partners", label: msg("Partner Network"), icon: Handshake, staff: true, section: "logiful" },
  { to: "/admin/changelog", label: msg("Neuerungen"), icon: Sparkles, staff: true, section: "logiful", badge: "changelog" },
  { to: "/admin/system", label: msg("Systemstatus"), icon: Activity, staff: true, section: "logiful", badge: "alerts" },
  { to: "/admin/team", label: msg("Logiful-Team"), icon: ShieldCheck, staff: true, section: "logiful" },
];


/** Liegt die Seite in den Einstellungen? (für die aufgeklappte Untergliederung in der Leiste) */
const isSettingsPath = (p: string) => /^\/(settings|channels|rules)(\/|$)/.test(p);

/** Navigation je Rolle – Kunden-Mitarbeiter sehen keine Verwaltungsbereiche */
export function visibleNav(me: Me, role: EffectiveRole, permissions?: Permissions, modules?: Partial<Record<ModuleKey, string[]>>, prospect?: ProspectAccess | null) {
  // Interessent (selbst registriert, noch nicht freigeschaltet): nur freigegebene Bereiche – Logiful sieht alles
  const shown = (to: string) => me.user.isStaff || !prospect || prospectAllowsPath(prospect, to);
  return NAV.filter((n) => shown(n.to) && (!n.manage || canManage(role)) && (!n.staff || me.user.isStaff) && (!n.area || !permissions || can(permissions, n.area, "view")) && (!n.module || me.user.isStaff || !!modules?.[n.module[0]]?.includes(n.module[1]))).map((n) => ({ ...n, label: t(n.label), ...(n.desc ? { desc: t(n.desc) } : {}) }));
}

/** Rechte im aktuellen Kunden: darf diese Person in einem Bereich lesen/bearbeiten? */
export function usePermission() {
  const { tenant } = useTenant();
  const p = tenant.permissions;
  return (area: Area, need: "view" | "edit" = "edit") => !p || can(p, area, need);
}

export function AppShell() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const { pathname: currentPath } = useLocation();
  // Sprache aus dem Profil übernehmen (lädt neu, falls abweichend)
  useEffect(() => syncProfileLocale(me.data?.user.locale), [me.data?.user.locale]);
  const [tenantId, setTenantId] = useState<string | null>(() => {
    try {
      return localStorage.getItem("lc-tenant");
    } catch {
      return null;
    }
  });
  const theme = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (me.isLoading) return <div className="h-screen grid place-items-center"><Logo /></div>;
  if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to={`/login${location.pathname !== "/" ? `?next=${encodeURIComponent(location.pathname + location.search)}` : ""}`} replace />;
  if (me.isError || !me.data) return <ConnectionProblem onRetry={() => void me.refetch()} />;
  if (me.data.mfa?.state === "pending") return <Navigate to="/login/mfa" replace />;
  if (me.data.mfa?.state === "setup") return <MfaRequiredScreen onDone={() => void me.refetch()} />;

  const tenant = me.data.tenants.find((t) => t.tenantId === tenantId) ?? me.data.tenants[0];
  if (!tenant) return <NoTenant me={me.data} onCreated={() => void me.refetch()} />;
  const select = (id: string) => {
    setTenantId(id);
    try {
      localStorage.setItem("lc-tenant", id);
    } catch {
      /* egal */
    }
  };

  return (
    <TenantContext.Provider value={{ me: me.data, tenantId: tenant.tenantId, tenant, role: tenant.role, setTenantId: select }}>
      <GuideProvider me={me.data}>
      <div className="flex flex-col h-screen"><StagingBanner />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Seitenleiste */}
        <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-surface">
          <div className="h-14 flex items-center px-4">
            <Logo />
          </div>
          <div className="px-3" data-tour="tenant">
            <TenantSwitcher />
          </div>
          <nav className="mt-4 px-3 flex-1 space-y-0.5 overflow-y-auto">
            <SideNav items={visibleNav(me.data, tenant.role, tenant.permissions, tenant.modules, tenant.prospect)} />
          </nav>
          <div className="p-3 border-t border-border space-y-2">
            {isDemo && (
              <div className="rounded-lg bg-coral-soft border border-coral/20 px-2.5 py-2 text-[12px] text-fg-muted">
                <span className="font-semibold text-coral">{t("Demo-Modus")}</span> {t("· Testdaten, keine Verbindung zu Shopify oder Lager")}
              </div>
            )}
            <div className="flex items-center gap-1">
              <button onClick={() => setPaletteOpen(true)} data-tour="search" className="flex-1 flex items-center gap-2 h-8 px-2.5 rounded-lg text-fg-muted hover:bg-surface-2 text-[13px]">
                <Search className="size-4" /> {t("Suchen")} <span className="ml-auto flex gap-0.5"><Kbd>⌘</Kbd><Kbd>K</Kbd></span>
              </button>
              <HelpButton tourAnchor="help" />
              <LanguageSwitch />
              <button onClick={theme.toggle} aria-label={t("Hell/Dunkel umschalten")} className="size-8 grid place-items-center rounded-lg text-fg-muted hover:bg-surface-2">
                {theme.dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
              </button>
            </div>
            <UserMenu />
          </div>
        </aside>

        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          <MobileBar onSearch={() => setPaletteOpen(true)} />
          {tenant.overdue && <OverdueBanner o={tenant.overdue} staff={me.data.user.isStaff} />}
          {tenant.prospect && !me.data.user.isStaff ? (
            <div role="status" className="sticky top-0 z-20 border-b border-coral/30 bg-coral/10 px-4 md:px-8 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2">
              <span className="font-semibold text-coral">{t("Willkommen bei Logiful")}</span>
              <span className="text-fg-muted">{t("Richtet in Ruhe alles ein – ans Lager geht erst etwas, wenn Logiful euer Konto freischaltet. Weitere Bereiche folgen mit der Freischaltung.")}</span>
            </div>
          ) : tenant.testModeSince && (
            <div role="status" className="sticky top-0 z-20 border-b border-warning/30 bg-warning-soft px-4 md:px-8 py-2 text-[12.5px] flex flex-wrap items-center gap-x-2">
              <span className="font-semibold text-warning">{t("Testbetrieb")}</span>
              <span className="text-fg-muted">{t("Aufträge werden geprüft, aber nicht ans Lager übergeben · keine Bestände und kein Standort an Shops · seit")} {new Date(tenant.testModeSince).toLocaleDateString(intlLocale())}</span>
            </div>
          )}
          {tenant.prospect && !me.data.user.isStaff && !prospectAllowsPath(tenant.prospect, currentPath) ? <ProspectLocked /> : <Outlet />}
        </main>
      </div>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} toggleTheme={theme.toggle} />
      {me.data.user.staffRole === "staff_admin" && <DevToolkit />}
      </GuideProvider>
    </TenantContext.Provider>
  );
}

/** Zwei-Faktor ist vorgeschrieben, aber noch nicht eingerichtet – erst einrichten, dann weiter */
function MfaRequiredScreen({ onDone }: { onDone: () => void }) {
  return (
    <div className="min-h-screen grid place-items-center px-4 py-10 bg-bg">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-6 shadow-sm">
        <Logo />
        <h1 className="mt-5 text-[22px] font-semibold tracking-tight">{t("Zwei-Faktor-Anmeldung einrichten")}</h1>
        <p className="mt-1.5 text-[13px] text-fg-muted">{t("Für dein Konto ist die Zwei-Faktor-Anmeldung vorgeschrieben. Das dauert eine Minute – danach geht es direkt weiter.")}</p>
        <div className="mt-5"><MfaSetupWizard onDone={onDone} /></div>
        <button className="mt-6 text-[12.5px] text-fg-muted hover:text-fg underline underline-offset-2" onClick={() => void api.auth.logout().then(() => location.assign("/login"))}>{t("Abmelden")}</button>
      </div>
    </div>
  );
}

/** Anzahl offener Punkte im Handlungsbedarf – rot, sobald etwas dringend ist */
/** Logiful: offene Störungen in der Navigation */
function ChangelogBadge() {
  const n = useUnseenChangelog();
  if (!n) return null;
  return <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-semibold tabular-nums bg-success-soft text-success" aria-label={t("{n} ungelesene Neuerungen", { n })}>{n}</span>;
}

function AlertsBadge() {
  const q = useQuery({ queryKey: ["alerts"], queryFn: api.admin.alerts, refetchInterval: 60_000 });
  const open = (q.data ?? []).filter((a) => !a.resolvedAt);
  if (!open.length) return null;
  const critical = open.some((a) => a.severity === "critical");
  return <span className={cx("ml-auto min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-semibold tabular-nums", critical ? "bg-danger text-white" : "bg-warning-soft text-warning")} aria-label={t("{length} offene Störungen", { length: open.length })}>{open.length}</span>;
}

function ActionBadge() {
  const q = useActionItems();
  const n = q.data?.items.length ?? 0;
  if (!n) return null;
  const critical = q.data!.items.some((i) => i.severity === "critical");
  return (
    <span className={cx("ml-auto min-w-[20px] h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-semibold tabular-nums", critical ? "bg-danger text-white" : "bg-warning-soft text-warning")} aria-label={t("{n} offene Punkte", { n })}>
      {n}
    </span>
  );
}

/**
 * Seitenleiste: Hauptbereiche, darunter ein Eintrag „Einstellungen“ (führt zur Übersicht) – die einzelnen
 * Einstellungen klappen nur auf, solange man sich in den Einstellungen bewegt.
 */
function SideNav({ items, mobile, onNavigate }: { items: NavItem[]; mobile?: boolean; onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const inSettings = isSettingsPath(pathname);
  // beim Betreten der Einstellungen aufgeklappt, beim Verlassen zu – dazwischen per Pfeil frei auf/zu
  const [open, setOpen] = useState(inSettings);
  useEffect(() => setOpen(inSettings), [inSettings]);
  const main = items.filter((n) => (n.section ?? "main") === "main");
  const settings = items.filter((n) => n.section === "settings");
  const logiful = items.filter((n) => n.section === "logiful");
  const row = cx("group relative flex items-center gap-2.5 px-2.5 rounded-lg font-medium transition-colors", mobile ? "h-10 text-[14px]" : "h-8 text-[13px]");
  const tone = (active: boolean) => (active ? "bg-surface-2 text-fg" : "text-fg-muted hover:text-fg hover:bg-surface-2/60");
  const link = (n: NavItem, sub = false) => (
    <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-tour={`nav:${n.to}`} className={({ isActive }) => cx(row, sub && (mobile ? "h-9 text-[13.5px]" : "h-7 text-[12.5px]"), tone(isActive))}>
      {({ isActive }) => (
        <>
          {isActive && <span className={cx("absolute top-1.5 bottom-1.5 w-[3px] rounded-full bg-coral", sub ? "-left-[9px]" : "left-0")} />}
          <n.icon className={cx(sub ? "size-3.5" : "size-4", isActive ? "text-fg" : "text-fg-subtle group-hover:text-fg-muted")} />
          <span className="truncate">{t(n.label)}</span>
          {n.soon && <span className="ml-auto text-[10.5px] text-fg-subtle">{t("bald")}</span>}
          {n.badge === "actions" && <ActionBadge />}{n.badge === "alerts" && <AlertsBadge />}{n.badge === "changelog" && <ChangelogBadge />}
        </>
      )}
    </NavLink>
  );
  return (
    <>
      {main.map((n) => link(n))}
      {settings.length > 0 && (
        <div className="pt-3">
          <div className="relative">
            <NavLink
              to="/settings"
              end
              data-tour="nav:/settings"
              // ganze Fläche: aufgeklappt → zuklappen (bleibt auf der Seite), zugeklappt → aufklappen und zur Übersicht
              onClick={(e) => { if (open) { e.preventDefault(); setOpen(false); return; } setOpen(true); onNavigate?.(); }}
              className={cx(row, "pr-9", tone(inSettings))}
            >
              {pathname === "/settings" && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-coral" />}
              <Settings className={cx("size-4", inSettings ? "text-fg" : "text-fg-subtle group-hover:text-fg-muted")} />
              {t("Einstellungen")}
            </NavLink>
            <button type="button" aria-expanded={open} aria-label={open ? t("Einstellungen zuklappen") : t("Einstellungen aufklappen")} onClick={() => setOpen((o) => !o)} className={cx("absolute right-1 top-1/2 -translate-y-1/2 grid place-items-center rounded-md text-fg-subtle hover:text-fg hover:bg-surface-2", mobile ? "size-8" : "size-6")}>
              <ChevronDown className={cx("size-3.5 transition-transform", !open && "-rotate-90")} />
            </button>
          </div>
          {open && <div className="ml-[17px] mt-0.5 border-l border-border pl-2 space-y-0.5">{settings.map((n) => link(n, true))}</div>}
        </div>
      )}
      {logiful.length > 0 && (
        <>
          <p data-tour="logiful" className="px-2.5 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">{t("Logiful intern")}</p>
          {logiful.map((n) => link(n))}
        </>
      )}
    </>
  );
}

/** Mobile: Kopfzeile mit Menü (Navigation als Overlay) */
function MobileBar({ onSearch }: { onSearch: () => void }) {
  const { me, role, tenant } = useTenant();
  const [open, setOpen] = useState(false);
  return (
    <div className="md:hidden sticky top-0 z-30 flex items-center gap-2 h-12 px-4 border-b border-border bg-surface/90 backdrop-blur">
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger className="size-8 -ml-1.5 grid place-items-center rounded-lg hover:bg-surface-2" aria-label={t("Menü öffnen")}><Menu className="size-5" /></Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
          <Dialog.Content aria-describedby={undefined} className="fixed z-50 left-0 top-0 h-full w-72 bg-surface border-r border-border p-3 animate-sheet-in">
            <Dialog.Title className="h-10 flex items-center px-1"><Logo /></Dialog.Title>
            <div className="mt-2"><TenantSwitcher /></div>
            <nav className="mt-4 space-y-0.5 overflow-y-auto max-h-[calc(100%-7rem)]">
              <SideNav mobile items={visibleNav(me, role, tenant.permissions, tenant.modules, tenant.prospect).filter((n) => !n.soon)} onNavigate={() => setOpen(false)} />
            </nav>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <Logo />
      <button onClick={onSearch} data-tour="search" className="ml-auto size-8 grid place-items-center rounded-lg hover:bg-surface-2" aria-label={t("Suchen")}><Search className="size-4" /></button>
      <HelpButton tourAnchor="help" />
    </div>
  );
}

/** Kürzel aus den ersten zwei Wörtern: „Carina Bachhuber“ → CB */
const initials = (name: string) => name.split(/[\s&+-]+/).filter((w) => /^[\p{L}\d]/u.test(w)).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";

function TenantLabel({ name, customerNumber }: { name: string; customerNumber?: string | null }) {
  return (
    <>
      <span className="size-8 shrink-0 rounded-lg bg-ink text-ink-fg grid place-items-center text-[11.5px] font-bold tracking-tight">{initials(name)}</span>
      <span className="min-w-0 flex-1 text-left leading-tight">
        <span className="block truncate font-semibold text-[13px]" title={name}>{name}</span>
        {customerNumber && <span className="block mt-0.5 text-[11px] text-fg-subtle tabular-nums">{t("Kd-Nr.")} {customerNumber}</span>}
      </span>
    </>
  );
}

function TenantSwitcher() {
  const { me, tenant, setTenantId } = useTenant();
  if (me.tenants.length < 2)
    return (
      <div className="flex items-center gap-2.5 px-2 py-1.5">
        <TenantLabel name={tenant.name} customerNumber={tenant.customerNumber} />
      </div>
    );
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="w-full flex items-center gap-2.5 min-h-12 px-2 py-1.5 rounded-xl border border-border bg-surface hover:bg-surface-2 data-[state=open]:bg-surface-2 text-left">
        <TenantLabel name={tenant.name} customerNumber={tenant.customerNumber} />
        <ChevronsUpDown className="shrink-0 size-3.5 text-fg-subtle" />
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="start" sideOffset={6} className="z-50 w-[var(--radix-dropdown-menu-trigger-width)] min-w-64 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-surface p-1 shadow-xl animate-pop-in">
          <Dropdown.Label className="px-2 py-1.5 text-[11.5px] text-fg-subtle">{t("Kunde wechseln")}</Dropdown.Label>
          {me.tenants.map((t) => (
            <Dropdown.Item
              key={t.tenantId}
              onSelect={() => setTenantId(t.tenantId)}
              className={cx("flex items-center gap-2 min-h-9 px-2 py-1 rounded-lg outline-none cursor-pointer data-[highlighted]:bg-surface-2", t.tenantId === tenant.tenantId && "font-semibold")}
            >
              <span className="size-6 shrink-0 rounded-md bg-surface-2 border border-border grid place-items-center text-[10px] font-bold">{initials(t.name)}</span>
              <span className="min-w-0 flex-1 truncate" title={t.name}>{t.name}</span>
              {t.customerNumber && <span className="shrink-0 text-[11px] font-normal text-fg-subtle tabular-nums">{t.customerNumber}</span>}
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

function UserMenu() {
  const { me } = useTenant();
  const navigate = useNavigate();
  return (
    <Dropdown.Root>
      <Dropdown.Trigger className="w-full flex items-center gap-2 h-9 px-2 rounded-lg hover:bg-surface-2 text-left">
        <span className="size-6 rounded-full bg-surface-2 border border-border grid place-items-center text-[11px] font-semibold">{(me.user.name ?? me.user.email).slice(0, 1).toUpperCase()}</span>
        <span className="truncate text-[12.5px] text-fg-muted">{me.user.name ?? me.user.email}</span>
        {me.user.isStaff && <Badge tone="coral" className="ml-auto">{t("Logiful")}</Badge>}
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content side="top" align="start" sideOffset={6} className="z-50 w-56 rounded-xl border border-border bg-surface p-1 shadow-xl animate-pop-in">
          <Dropdown.Label className="px-2 py-1.5 text-[11.5px] text-fg-subtle truncate">{me.user.email}</Dropdown.Label>
          <Dropdown.Item onSelect={() => navigate("/settings")} className="flex items-center gap-2 h-8 px-2 rounded-lg outline-none cursor-pointer data-[highlighted]:bg-surface-2">
            <Settings className="size-4 text-fg-subtle" /> {t("Einstellungen")}
          </Dropdown.Item>
          <Dropdown.Separator className="my-1 h-px bg-border" />
          <Dropdown.Label className="px-2 pt-1 pb-0.5 text-[11px] text-fg-subtle flex items-center gap-1.5"><Languages className="size-3.5" /> {t("Sprache")}</Dropdown.Label>
          {LOCALES.map((l) => (
            <Dropdown.Item
              key={l.id}
              onSelect={() => void api.auth.setLocale(l.id).catch(() => undefined).finally(() => setLocale(l.id))}
              className="flex items-center gap-2 h-8 px-2 rounded-lg outline-none cursor-pointer data-[highlighted]:bg-surface-2"
            >
              <span className="size-4 grid place-items-center">{getLocale() === l.id && <Check className="size-3.5" />}</span> {t(l.label)}
            </Dropdown.Item>
          ))}
          <Dropdown.Separator className="my-1 h-px bg-border" />
          <Dropdown.Item
            onSelect={() => void api.auth.logout().finally(() => location.assign("/login"))}
            className="flex items-center gap-2 h-8 px-2 rounded-lg outline-none cursor-pointer data-[highlighted]:bg-surface-2"
          >
            <LogOut className="size-4 text-fg-subtle" /> {t("Abmelden")}
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

/**
 * Noch kein Mandant: Logiful-Mitarbeiter legen direkt den ersten Kunden an,
 * Kunden-Benutzer ohne Zuordnung bekommen eine klare Erklärung.
 */
function NoTenant({ me, onCreated }: { me: Me; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const logout = () => void api.auth.logout().finally(() => location.assign("/login"));
  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md text-center animate-fade-in">
        <Logo className="justify-center" />
        {me.user.isStaff ? (
          <>
            <div className="mt-10 mx-auto size-12 rounded-2xl bg-coral-soft grid place-items-center"><Building2 className="size-6 text-coral" /></div>
            <h1 className="mt-5 text-[24px] font-semibold tracking-tight">{t("Willkommen")}{me.user.name ? `, ${me.user.name.split(" ")[0]}` : ""}!</h1>
            <p className="mt-2 text-fg-muted">{t("Du bist als Logiful-Admin angemeldet. Lege jetzt den ersten Kunden an – der Kunden-Admin bekommt eine Einladung und kann sein Team selbst verwalten.")}</p>
            {me.user.staffRole === "staff_admin" ? (
              <button onClick={() => setOpen(true)} className="mt-6 h-10 px-5 rounded-lg bg-ink text-ink-fg font-medium inline-flex items-center gap-2"><Building2 className="size-4" /> {t("Ersten Kunden anlegen")}</button>
            ) : (
              <p className="mt-6 text-[13px] text-fg-muted">{t("Kunden können nur Logiful-Admins anlegen.")}</p>
            )}
            <NewCustomerDialog open={open} onOpenChange={setOpen} onCreated={onCreated} />
          </>
        ) : (
          <>
            <h1 className="mt-10 text-[22px] font-semibold tracking-tight">{t("Noch kein Zugang zu einem Kunden")}</h1>
            <p className="mt-2 text-fg-muted">{t("Dein Konto")} <span className="font-medium text-fg">{me.user.email}</span> {t("ist angemeldet, aber noch keinem Unternehmen zugeordnet. Bitte deinen Administrator, dich einzuladen, oder wende dich an support@logiful.de.")}</p>
          </>
        )}
        <button onClick={logout} className="mt-8 inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg"><LogOut className="size-3.5" /> {t("Abmelden")}</button>
      </div>
    </div>
  );
}

function ConnectionProblem({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="h-screen grid place-items-center p-6">
      <div className="w-full max-w-sm text-center">
        <Logo className="justify-center" />
        <div className="mt-8 mx-auto size-11 rounded-xl bg-surface-2 border border-border grid place-items-center text-fg-muted"><WifiOff className="size-5" /></div>
        <h1 className="mt-4 text-xl font-semibold">{t("Keine Verbindung zum Portal")}</h1>
        <p className="mt-2 text-fg-muted">{t("Bitte prüfe deine Internetverbindung. Wir versuchen es automatisch weiter.")}</p>
        <button onClick={onRetry} className="mt-6 h-9 px-4 rounded-lg bg-ink text-ink-fg font-medium">{t("Erneut versuchen")}</button>
      </div>
    </div>
  );
}

// ── Befehlspalette (⌘K) ───────────────────────────────────────────────
function CommandPalette({ open, onOpenChange, toggleTheme }: { open: boolean; onOpenChange: (o: boolean) => void; toggleTheme: () => void }) {
  const navigate = useNavigate();
  const { tenantId, me, role, tenant } = useTenant();
  const [search, setSearch] = useState("");
  const results = useQuery({
    queryKey: ["palette", tenantId, search],
    queryFn: () => api.products.list(tenantId, { search, pageSize: 6 }),
    enabled: open && search.trim().length >= 2,
  });

  const go = (to: string) => {
    onOpenChange(false);
    setSearch("");
    navigate(to);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-[14vh] -translate-x-1/2 w-[min(640px,calc(100vw-32px))] rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in overflow-hidden">
          <Dialog.Title className="sr-only">{t("Befehlspalette")}</Dialog.Title>
          <Command shouldFilter={false} label={t("Befehlspalette")} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11.5px] [&_[cmdk-group-heading]]:text-fg-subtle">
            <div className="flex items-center gap-2 px-4 border-b border-border">
              <Search className="size-4 text-fg-subtle" />
              <Command.Input value={search} onValueChange={setSearch} placeholder={t("Artikel, SKU oder EAN suchen – oder Befehl eingeben …")} className="h-12 flex-1 bg-transparent outline-none text-[14px] placeholder:text-fg-subtle" />
              <Kbd>{t("Esc")}</Kbd>
            </div>
            <Command.List className="max-h-[50vh] overflow-y-auto p-1.5 pb-2">
              <Command.Empty className="py-8 text-center text-fg-muted">{t("Nichts gefunden.")}</Command.Empty>
              {results.data && results.data.items.length > 0 && (
                <Command.Group heading="Artikel">
                  {results.data.items.map((p) => (
                    <PaletteItem key={p.id} onSelect={() => go(`/products?open=${p.id}`)}>
                      <Package className="size-4 text-fg-subtle" />
                      <span className="font-mono text-[12px] text-fg-muted w-36 truncate">{p.sku}</span>
                      <span className="truncate">{p.name}</span>
                      <span className="ml-auto"><ValidationBadge status={p.validation.status} /></span>
                    </PaletteItem>
                  ))}
                </Command.Group>
              )}
              <Command.Group heading="Navigation">
                {visibleNav(me, role, tenant.permissions, tenant.modules, tenant.prospect).filter((n) => !n.soon && (!search || n.label.toLowerCase().includes(search.toLowerCase()))).map((n) => (
                  <PaletteItem key={n.to} onSelect={() => go(n.to)}>
                    <n.icon className="size-4 text-fg-subtle" /> {t(n.label)}
                  </PaletteItem>
                ))}
              </Command.Group>
              {(!search || "blockiert fehler gewicht".includes(search.toLowerCase())) && (
                <Command.Group heading="Schnellfilter">
                  <PaletteItem onSelect={() => go("/products?validation=invalid")}>{t("Blockierte Artikel anzeigen")}</PaletteItem>
                  <PaletteItem onSelect={() => go("/products?rule=weight.required")}>{t("Artikel ohne Gewicht")}</PaletteItem>
                  <PaletteItem onSelect={() => go("/orders?status=on_hold")}>{t("Zurückgehaltene Aufträge")}</PaletteItem>
                </Command.Group>
              )}
              <Command.Group heading="Aktionen">
                <PaletteItem onSelect={() => go("/channels?connect=1")}>
                  <PlugZap className="size-4 text-fg-subtle" /> {t("Shopify-Shop verbinden")}
                </PaletteItem>
                <PaletteItem onSelect={() => { toggleTheme(); onOpenChange(false); }}>
                  <Moon className="size-4 text-fg-subtle" /> {t("Hell/Dunkel umschalten")}
                </PaletteItem>
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PaletteItem({ children, onSelect }: { children: ReactNode; onSelect: () => void }) {
  return (
    <Command.Item onSelect={onSelect} className="flex items-center gap-2.5 h-9 px-2.5 rounded-lg cursor-pointer text-[13px] data-[selected=true]:bg-surface-2">
      {children}
    </Command.Item>
  );
}

/** Einheitlicher Seitenkopf */
export function PageHeader({ title, description, actions, icon }: { title: string; description?: ReactNode; actions?: ReactNode; icon?: ReactNode }) {
  const { pathname } = useLocation();
  const hasHelp = !!helpFor(pathname);
  return (
    <header className="flex flex-wrap items-end gap-3 px-4 md:px-8 pt-6 md:pt-8 pb-5">
      {icon}
      <div className="min-w-0">
        <h1 className="text-[22px] font-semibold tracking-tight flex items-center gap-1.5">{t(title)}{hasHelp && <HelpButton className="size-7 text-fg-subtle" />}</h1>
        {description && <p className="text-fg-muted mt-1">{description}</p>}
      </div>
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Sprache unten links: Kürzel der aktuellen Sprache, Auswahl als kleines Menü (wird im Profil gemerkt) */
function LanguageSwitch() {
  const cur = getLocale();
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button aria-label={t("Sprache")} title={t("Sprache")} className="h-8 px-1.5 grid place-items-center rounded-lg text-fg-muted hover:bg-surface-2 data-[state=open]:bg-surface-2 text-[11.5px] font-semibold tracking-wide uppercase">
          {cur}
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content side="top" align="end" sideOffset={6} className="z-50 w-40 rounded-xl border border-border bg-surface p-1.5 shadow-xl animate-pop-in">
          {LOCALES.map((l) => (
            <Dropdown.Item
              key={l.id}
              onSelect={() => void api.auth.setLocale(l.id).catch(() => undefined).finally(() => setLocale(l.id))}
              className="flex items-center gap-2 h-8 px-2 rounded-lg text-[13px] outline-none cursor-pointer data-[highlighted]:bg-surface-2"
            >
              <span className="w-6 text-[11px] font-semibold uppercase text-fg-subtle">{l.id}</span>
              {t(l.label)}
              {cur === l.id && <Check className="size-3.5 ml-auto" />}
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
